// OWNED BY the tasks tab agent (created under detail/tabs/tasks/).
//
// One task row per mock s20: checkbox completion (optimistic via Firestore
// latency compensation), due caption, optional listing chip and a
// postpone-to-today action (ported from CalendarTasksRow). Owner-only editing
// guard ported from CalendarTasksRow; writes prefer event.path over rebuilding
// the path from the user email (per the calendar-page convention).
//
// The mock's trailing assignee avatar was dropped: these rows live inside ONE
// realtor's own calendar, so it rendered the same face on every row. A task
// owned by someone else is still identified — by the (email) suffix next to
// the title, which is the only case where the assignee is not the viewer.

import {
  Chip,
  alpha,
  Stack,
  Tooltip,
  Checkbox,
  Typography,
  IconButton
} from "@material-ui/core";
import { Today } from "@material-ui/icons";
import useAuth from "hooks/useAuth";
import { useSnackbar } from "notistack5";
import { adjustTaskTimeForToday } from "utils/calendar/helper";
import {
  eventDocRef,
  formatTaskDueLabel,
  isOwnCalendarEvent,
  patchCachedOwnCalendarEvents
} from "./contactTasksData";
import type { ContactTaskGroupId } from "./contactTasksData";
import type { MouseEvent } from "react";
import type { CalendarEvent, CalendarEventStatus } from "../../../../../../@types/sharedSchema";

type ContactTaskRowProps = {
  task: CalendarEvent;
  group: ContactTaskGroupId;
  /** CalendarContext live events loaded — required for opening the editor dialog. */
  live: boolean;
  onEdit: (task: CalendarEvent) => void;
};

export function ContactTaskRow({ task, group, live, onEdit }: ContactTaskRowProps): JSX.Element {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const userEmail: string = user?.email ?? "";

  const isOwner = isOwnCalendarEvent(task, userEmail);
  const done = task.status === "done";
  const overdue = group === "overdue";
  // A row without a doc ref (optimistically cached quick-add before the live
  // listener emitted) can't be written to yet — its actions are disabled.
  const persisted = !!(task.id || task.path);
  const canOpenEditor = live && isOwner && !!task.id;

  // While the tab renders from the session-cache snapshot (live listener not
  // yet emitted), Firestore latency compensation has no listener to feed — so
  // writes would be invisible until live takes over. Patch the cached array
  // directly for immediate feedback; once live, the mirror overwrites it.
  const patchLocalTask = (patch: Partial<CalendarEvent>): void => {
    if (live || !task.id) return;
    patchCachedOwnCalendarEvents((events) =>
      events.map((event) => (event.id === task.id ? { ...event, ...patch } : event))
    );
  };

  const handleToggleDone = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (!isOwner || !persisted) return;

    const nextStatus: CalendarEventStatus = done ? "pending" : "done";

    // Optimistic by design: once live, the own-calendar onSnapshot fires
    // immediately with the local write (Firestore latency compensation);
    // in the cold window the cache patch provides the same instant feedback.
    patchLocalTask({ status: nextStatus });

    try {
      await eventDocRef(task, userEmail).update({ status: nextStatus });
    } catch {
      patchLocalTask({ status: task.status });
      enqueueSnackbar("עדכון המשימה נכשל", { variant: "error", autoHideDuration: 4000 });
    }
  };

  const handlePostponeToToday = async (event: MouseEvent): Promise<void> => {
    event.stopPropagation();
    if (!isOwner || !persisted) return;

    const { start, end } = adjustTaskTimeForToday(new Date(task.start), new Date(task.end));

    patchLocalTask({ start, end });

    try {
      await eventDocRef(task, userEmail).update({ start, end });
    } catch {
      patchLocalTask({ start: task.start, end: task.end });
      enqueueSnackbar("דחיית המשימה נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
  };

  // Ported visibility rule: postpone appears on overdue rows and on rows whose
  // due date is beyond today (disjoint groups already guarantee the latter).
  const showPostpone =
    !done &&
    isOwner &&
    persisted &&
    (group === "overdue" || group === "thisWeek" || group === "upcoming");

  const firstListing = task.listingsData?.[0];
  let listingLabel: string | null = null;

  if (firstListing?.address) {
    const { street, neighborhood, city } = firstListing.address;
    listingLabel =
      (street ? [street, city] : [neighborhood, city]).filter(Boolean).join(", ") || null;
  }

  const extraListingsCount = (task.listingsData?.length ?? 0) - 1;

  const strikeStyle = done ? { textDecoration: "line-through", color: "text.disabled" } : undefined;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      onClick={canOpenEditor ? () => onEdit(task) : undefined}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        cursor: canOpenEditor ? "pointer" : "default",
        "&:hover": canOpenEditor ? { bgcolor: "action.hover" } : undefined,
        ...(overdue && {
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.04)
        })
      }}
    >
      <Checkbox
        size="small"
        checked={done}
        disabled={!isOwner || !persisted}
        onClick={handleToggleDone}
        sx={overdue ? { color: "error.main" } : undefined}
        inputProps={{ "aria-label": done ? "סימון כלא הושלמה" : "סימון כהושלמה" }}
      />
      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" noWrap dir="auto" sx={strikeStyle}>
          {task.title}
          {!isOwner && !!task.userId && (
            <Typography component="span" variant="caption" sx={{ color: "text.disabled" }}>
              {" "}
              ({task.userId})
            </Typography>
          )}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: overdue ? "error.main" : "text.secondary" }}
        >
          {formatTaskDueLabel(task, group)}
        </Typography>
      </Stack>

      {listingLabel && (
        <Chip
          size="small"
          variant="outlined"
          label={`🔗 ${listingLabel}${extraListingsCount > 0 ? ` +${extraListingsCount}` : ""}`}
          sx={{ maxWidth: 180 }}
        />
      )}

      {showPostpone && (
        // "דחייה" (postpone) only fits overdue rows; for future rows (השבוע /
        // בהמשך) the same action PULLS the task earlier — label it as such.
        <Tooltip title={overdue ? "דחייה להיום" : "הקדמה להיום"}>
          <span>
            <IconButton size="small" onClick={handlePostponeToToday}>
              <Today fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
